import React, { useState } from 'react';
import { Mail, Phone, Smartphone, MessageSquare, PhoneCall, Newspaper, Calendar, FileText, Send, AtSign } from 'lucide-react';
import { PropSelectRow, PropTextRow, PropToggleRow } from './rows';
import { useSettingsStore } from '../../../lib/stores/settingsStore';
import {
  NEWSLETTER_COMPANY, NEWSLETTER_OTHER, currentNewsletterRecipient, newsletterRecipientOptions,
} from '../../../lib/newsletter/newsletterRecipient';

interface RowsProps {
  formData: Record<string, any>;
  updateFormData: (updates: Record<string, any>) => void;
  validationErrors: Record<string, string>;
}

const rowIcon = (Icon: React.ComponentType<any>) => <Icon className="h-3 w-3" strokeWidth={1.5} />;

/** Recapiti diretti del cliente a righe di proprietà (email, telefono, cellulare). */
export function ContactRows({ formData, updateFormData, validationErrors }: RowsProps) {
  // Pill del prefisso internazionale: preimpostato dal paese, sempre modificabile
  const prefixPill = (
    <input
      type="text"
      value={formData.country_code || ''}
      onChange={(e) => updateFormData({ country_code: e.target.value })}
      aria-label="Prefisso internazionale"
      className="w-11 flex-shrink-0 rounded border border-[rgba(55,53,47,0.16)] dark:border-white/15 bg-transparent px-1 text-center text-[11.5px] tabular-nums text-[#787774] dark:text-gray-400 focus:border-[#2383E2] focus:outline-none"
    />
  );

  return (
    <div>
      <PropTextRow
        icon={rowIcon(Mail)}
        label="Email"
        type="email"
        value={formData.email || ''}
        onChange={(value) => updateFormData({ email: value })}
        error={validationErrors.email}
      />
      <PropTextRow
        icon={rowIcon(Phone)}
        label="Telefono"
        lead={prefixPill}
        value={formData.phone || ''}
        onChange={(value) => updateFormData({ phone: value })}
        />
      <PropTextRow
        icon={rowIcon(Smartphone)}
        label="Cellulare"
        lead={prefixPill}
        value={formData.mobile || ''}
        onChange={(value) => updateFormData({ mobile: value })}
        />
    </div>
  );
}

/**
 * Destinatario della newsletter (come la vecchia scheda "Email Newsletter"): email
 * generica dell'anagrafica, un referente oppure un altro indirizzo. Scrive
 * newsletter_email (vuoto = email principale).
 */
function NewsletterRecipientRows({ formData, updateFormData }: Omit<RowsProps, 'validationErrors'>) {
  const [otherMode, setOtherMode] = useState(false);
  const options = newsletterRecipientOptions({ email: formData.email, entity_type: formData.entity_type }, formData.contacts);
  const derived = currentNewsletterRecipient(
    { newsletter_subscribed: true, newsletter_email: formData.newsletter_email, email: formData.email },
    formData.contacts,
  );
  const value = otherMode || derived === NEWSLETTER_OTHER ? NEWSLETTER_OTHER : derived;
  const selectOptions = [
    ...options.map((o) => ({ value: o.value, label: `${o.label} · ${o.email}` })),
    { value: NEWSLETTER_OTHER, label: 'Altro indirizzo' },
  ];

  return (
    <>
      <PropSelectRow
        icon={rowIcon(Send)}
        label="Destinatario"
        value={selectOptions.some((o) => o.value === value) ? value || '' : ''}
        onChange={(v) => {
          if (v === NEWSLETTER_OTHER) {
            setOtherMode(true);
            if (derived !== NEWSLETTER_OTHER) updateFormData({ newsletter_email: '' });
            return;
          }
          setOtherMode(false);
          const opt = options.find((o) => o.value === v);
          updateFormData({ newsletter_email: v === NEWSLETTER_COMPANY || !opt ? '' : opt.email });
        }}
        options={selectOptions}
        placeholder="Scegli chi riceve la newsletter"
      />
      {value === NEWSLETTER_OTHER && (
        <PropTextRow
          icon={rowIcon(AtSign)}
          label="Email newsletter"
          type="email"
          value={formData.newsletter_email || ''}
          onChange={(v) => updateFormData({ newsletter_email: v })}
          placeholder="newsletter@azienda.it"
        />
      )}
    </>
  );
}

/** Consensi marketing a righe: click sul valore per attivare/disattivare. */
export function ConsentRows({ formData, updateFormData }: RowsProps) {
  // Newsletter unificata per email (opzione per org): niente destinatario, la newsletter va all'email della scheda
  const unified = useSettingsStore((s) => s.settings?.newsletter_unified_by_email === true);
  return (
    <div>
      <PropToggleRow
        icon={rowIcon(Mail)}
        label="Email marketing"
        checked={!!formData.marketing_consent_email}
        onChange={(checked) => updateFormData({ marketing_consent_email: checked })}
      />
      <PropToggleRow
        icon={rowIcon(MessageSquare)}
        label="SMS marketing"
        checked={!!formData.marketing_consent_sms}
        onChange={(checked) => updateFormData({ marketing_consent_sms: checked })}
      />
      <PropToggleRow
        icon={rowIcon(PhoneCall)}
        label="Chiamate marketing"
        checked={!!formData.marketing_consent_phone}
        onChange={(checked) => updateFormData({ marketing_consent_phone: checked })}
      />
      <PropToggleRow
        icon={rowIcon(Newspaper)}
        label="Newsletter"
        checked={!!formData.newsletter_consent}
        onChange={(checked) => updateFormData({ newsletter_consent: checked })}
        onLabel="Iscrizione attiva"
        offLabel="Non iscritto"
      />
      {formData.newsletter_consent && !unified && (
        <NewsletterRecipientRows formData={formData} updateFormData={updateFormData} />
      )}
      <PropTextRow
        icon={rowIcon(Calendar)}
        label="Data consenso"
        type="date"
        value={formData.marketing_consent_date || ''}
        onChange={(value) => updateFormData({ marketing_consent_date: value })}
        placeholder=""
      />
      <PropTextRow
        icon={rowIcon(FileText)}
        label="Note marketing"
        value={formData.marketing_notes || ''}
        onChange={(value) => updateFormData({ marketing_notes: value })}
      />
    </div>
  );
}
